import { useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../../lib/AuthContext";
import Header from "../../components/Header";
import {
  ChartBar, MagnifyingGlass, BookmarkSimple, Broadcast, ListChecks,
  CreditCard, GearSix, CircleNotch, Crown, Scales,
} from "@phosphor-icons/react";

const navItems = [
  { to: "/dashboard", icon: ChartBar, label: "Overview", exact: true },
  { to: "/dashboard/history", icon: MagnifyingGlass, label: "History" },
  { to: "/dashboard/saved", icon: BookmarkSimple, label: "Saved" },
  { to: "/dashboard/monitoring", icon: Broadcast, label: "Monitoring" },
  { to: "/dashboard/bulk", icon: ListChecks, label: "Bulk" },
  { to: "/dashboard/reconcile", icon: Scales, label: "Reconcile" },
  { to: "/dashboard/subscription", icon: CreditCard, label: "Plan" },
  { to: "/dashboard/settings", icon: GearSix, label: "Settings" },
];

const tierColors = {
  free: "bg-slate-200 text-slate-600",
  pro: "bg-teal/20 text-teal",
  team: "bg-violet-100 text-violet-700",
  enterprise: "bg-amber-100 text-amber-700",
};

export default function DashboardLayout() {
  const { user, isAuthenticated, loading, login } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      login({ appState: { returnTo: "/dashboard" } });
    }
  }, [loading, isAuthenticated, login]);

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-[80vh] bg-slate-50 flex items-center justify-center">
          <CircleNotch className="w-8 h-8 text-teal animate-spin" />
        </div>
      </>
    );
  }

  if (!isAuthenticated) return <Header />;

  if (!user) {
    return (
      <>
        <Header />
        <div className="min-h-[80vh] bg-slate-50 flex items-center justify-center px-4">
          <div className="text-center max-w-sm">
            <CircleNotch className="w-8 h-8 text-teal animate-spin mx-auto mb-4" />
            <h2 className="font-display font-bold text-lg text-slate-900 mb-2">Setting up your dashboard...</h2>
            <p className="text-sm text-slate-500 mb-4">This should only take a moment.</p>
            <button onClick={() => window.location.reload()} className="text-sm text-teal font-medium hover:underline">Refresh if stuck</button>
          </div>
        </div>
      </>
    );
  }

  const isActive = (path, exact) => exact ? location.pathname === path : location.pathname.startsWith(path);
  const tier = user.tier || "free";
  const config = user.tier_config || {};
  const lookupLimit = config.lookup_limit || 10;
  const lookups = user.lookup_count_today || 0;
  const showUsage = lookupLimit > 0 && lookupLimit !== -1;

  const initials = user.name
    ? user.name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)
    : "?";

  return (
    <>
      <Header />
      <div className="bg-slate-50 min-h-[calc(100vh-56px)]">
        {/* Dashboard header strip */}
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* User info row */}
            <div className="flex items-center justify-between py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-navy text-white flex items-center justify-center text-sm font-bold shrink-0">
                  {initials}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="font-display font-bold text-lg text-slate-900">{user.name || "Dashboard"}</h1>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${tierColors[tier]}`}>
                      {tier !== "free" && <Crown className="w-2.5 h-2.5" weight="fill" />}
                      {tier}
                    </span>
                  </div>
                  {showUsage && (
                    <div className="flex items-center gap-2 mt-0.5">
                      <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${lookups >= lookupLimit ? "bg-red-500" : lookups >= lookupLimit * 0.8 ? "bg-amber-500" : "bg-teal"}`}
                          style={{ width: `${Math.min(100, (lookups / lookupLimit) * 100)}%` }}
                        />
                      </div>
                      <span className="text-[11px] text-slate-400">{lookups}/{lookupLimit} lookups</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Tab navigation */}
            <nav className="flex items-center gap-0.5 -mb-px overflow-x-auto scrollbar-hide" data-testid="dashboard-tabs">
              {navItems.map(({ to, icon: Icon, label, exact }) => {
                const active = isActive(to, exact);
                return (
                  <Link
                    key={to}
                    to={to}
                    data-testid={`dash-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
                    className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                      active
                        ? "border-navy text-navy"
                        : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                    }`}
                  >
                    <Icon className="w-4 h-4 shrink-0" weight={active ? "fill" : "regular"} />
                    <span className="hidden sm:inline">{label}</span>
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Outlet />
        </div>
      </div>
    </>
  );
}
